import Vue from "vue";
import App from "./App.vue";
import "./registerServiceWorker";
import router from "./router";

// @ts-ignore
import VueToastr from "vue-toastr";
import VueI18n from "vue-i18n";

import "@fortawesome/fontawesome-free/css/all.css";
import "./assets/css/wallet_adapter.css";

import "@/assets/css/theme.css";
import "@/assets/css/perfectui.css";
import * as en from "./locales/en.json";
import store from "./store";
import PortalVue from "portal-vue";
import { Route } from "vue-router";

Vue.config.productionTip = false;

Vue.use(PortalVue);

Vue.use(VueToastr, {
  defaultPosition: "toast-bottom-right",
  timeout: 2000,
  preventDuplicates: true,
});

Vue.use(VueI18n);

const messages = {
  en,
};
const i18n = new VueI18n({
  locale: "en",
  messages,
});

const noAuth = {
  "/": true,
  "/login": true,
  "/register": true,
  "/guide": true,
  "/explorer": true,
  "/privacy": true,
  "/terms-and-conditions": true,
};

router.beforeEach((to, from, next) => {
  const basePath = `/${to.path.split("/")[1]}`;

  // @ts-ignore
  if (!noAuth[to.path] && !noAuth[basePath] && !store.state.wallet.connected) {
    return unauthorized(to, from, next);
  }

  if (
    (basePath === "/worlds" || basePath === "/village") &&
    !store.state.user.world &&
    to.path !== "/worlds"
  ) {
    return worldSelect(to, from, next);
  }

  if (basePath === "/village" && !store.state.user.active_village) {
    if (store.state.user?.villages?.length > 0)
      return next({ path: `/worlds/village/select`, replace: true });
    else return redirectTo("/worlds/join", to, from, next);
  }

  next();
});

router.afterEach((to) => {
  updateDocumentTitle(to);
});

store.subscribe((mutation) => {
  if (mutation.type === "village/set_village" || mutation.type === "user/set_active_village") {
    updateDocumentTitle(router.currentRoute);
  }
});

const PAGE_TITLE_PREFIX = "Chrono Kingdoms";

function updateDocumentTitle(to: Route): void {
  document.title = getPageTitle(to);
}

function getPageTitle(to: Route): string {
  const pageTitle = getRoutePageTitle(to);
  return pageTitle ? `${pageTitle} | ${PAGE_TITLE_PREFIX}` : PAGE_TITLE_PREFIX;
}

function getRoutePageTitle(to: Route): string {
  const path = to.path;
  const parts = path.split("/").filter(Boolean);

  if (path === "/") return "Home";
  if (path === "/worlds/village/select") return "Select Village";
  if (parts[0] === "worlds" && parts[1] === "village" && parts[3] === "info") return "Village Info";
  if (path === "/village" || path.indexOf("/village/") === 0) return getVillagePageTitle(parts);

  if (path.indexOf("/explorer/") === 0) {
    if (parts[2] === "reports") return "Explorer Report";
    if (parts[2] === "movements" && parts[3]) return "Explorer Movement";
    if (parts[2] === "movements") return "Explorer Movements";
    if (parts[2] === "highscores") return "Explorer Highscores";
    if (parts[2] === "trades") return "Explorer Trades";
    if (parts[2] === "awards") return "Explorer Awards";
    return "World Explorer";
  }

  if (path.indexOf("/guide/") === 0) {
    return `Guide - ${formatTitle(parts[1])}`;
  }

  if (path.indexOf("/tribes/") === 0) {
    if (parts[2] === "diplomacy") return "Tribe Diplomacy";
    return "Tribe";
  }

  if (path.indexOf("/users/") === 0) return "Player";
  if (path.indexOf("/simulation/") === 0) return "Simulation";

  if (typeof to.name === "string" && to.name) {
    return formatTitle(to.name);
  }

  return parts.length ? formatTitle(parts[parts.length - 1]) : "";
}

function getVillagePageTitle(parts: string[]): string {
  const villageName = getActiveVillageName();
  const sectionTitle = getVillageSectionTitle(parts);

  if (!sectionTitle) return villageName || "Village";
  if (!villageName) return sectionTitle;
  return `${villageName} - ${sectionTitle}`;
}

function getVillageSectionTitle(parts: string[]): string {
  const label = parts[1];
  if (!label) return "";
  if (label === "tx-reports" || label === "reports") return "Attack Report";
  if (label === "movements" && parts[2]) return "Send Movement";
  if (label === "movements") return "Movements";
  if (label === "transport") return "Send Resources";
  if (label === "hq") return "Headquarters";
  if (label === "stable" || label === "stables") return "Stables";
  if (label === "siege" || label === "workshop") return "Workshop";
  return formatTitle(label);
}

function getActiveVillageName(): string {
  const village = store.state.village?.village;
  const client = store.state.user?.client;
  if (village?.name && client?.toName) return client.toName(village.name);
  return "";
}

function formatTitle(value: string): string {
  return value
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function worldSelect(to: Route, from: Route, next: Function) {
  return redirectTo(`/worlds`, to, from, next);
}

function unauthorized(to: Route, from: Route, next: Function) {
  let loginType = "";
  if (to.query.lt) loginType = `/${to.query.lt}`;

  return redirectTo(`${loginType}/login`, to, from, next);
}

function redirectTo(base: String, to: Route, from: Route, next: Function) {
  let query = "";
  //Add other query strings
  const keys = Object.keys(to.query);
  for (let i = 0; i < keys.length; i++) {
    query += `${i === 0 ? "?" : "&"}${keys[i]}=${to.query[keys[i]]}`;
  }

  const path = `${base}?path=${encodeURIComponent(`${to.path}?${query}`)}`;
  if (from.fullPath === path) {
    return next(false);
  }

  console.debug("Router Redirect", path);
  return next({ path, replace: true });
}

new Vue({
  i18n,
  router,
  store,

  // store,
  render: (h) => h(App),
}).$mount("#app");
